import {
  Button,
  Collapse,
  Select,
  Stack,
  Text,
  useDisclosure,
} from '@cardboard-ui/react';
import { Plural, t } from '@lingui/macro';
import {
  MemberSelector,
  MemberSelectorMember,
} from 'apps/TenantApp/components/MemberSelector';
import { EditIcon } from 'components/icons';
import React, { ChangeEvent, useState } from 'react';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';

interface ReminderPickerProps {
  label?: string;
  options: {
    value: number;
    label: string;
    visible: boolean;
  }[];
  availableMembers: MemberSelectorMember[];
  selectedMembers: MemberSelectorMember[];
  quickSelect: number;
  confirmQuickSelectChange?: boolean;
  onQuickSelectChange: (value: number) => void;
  onManualSelectChange: (value: MemberSelectorMember[]) => void;
}

export const ReminderPicker = ({
  label,
  options,
  availableMembers,
  selectedMembers,
  quickSelect,
  onQuickSelectChange,
  confirmQuickSelectChange,
  onManualSelectChange,
}: ReminderPickerProps) => {
  const {
    isOpen: isConfirmationOpen,
    onOpen: onOpenConfirmation,
    onClose: onCloseConfirmation,
  } = useDisclosure();

  const [showMemberPicker, setShowMemberPicker] = useState(false);
  const [tempQuickSelect, setTempQuickSelect] = useState(0);

  return (
    <Stack spacing={4}>
      <ConfirmationDialog
        action={{
          name: t`Edit quick select`,
          onAction: () => {
            onQuickSelectChange(tempQuickSelect);
            setShowMemberPicker(false);
            onCloseConfirmation();
          },
        }}
        isOpen={isConfirmationOpen}
        onClose={onCloseConfirmation}
      >
        {t`This step will reset the list.`}
      </ConfirmationDialog>

      {label && <Text fontWeight="bold">{label}</Text>}
      <Select
        size="md"
        placeholder="Quick select..."
        value={quickSelect}
        onChange={(event: ChangeEvent<HTMLSelectElement>) => {
          setShowMemberPicker(false);

          const newQuickSelect = parseInt(event.target.value);
          if (selectedMembers.length > 0 && confirmQuickSelectChange) {
            setTempQuickSelect(newQuickSelect);
            onOpenConfirmation();
          } else {
            onQuickSelectChange(newQuickSelect);
          }
        }}
      >
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            hidden={!option.visible}
          >
            {option.label}
          </option>
        ))}
      </Select>

      {!showMemberPicker && (
        <Button
          onClick={() => {
            setShowMemberPicker(true);
          }}
          rightIcon={<EditIcon />}
        >
          {quickSelect ? (
            <Plural
              value={selectedMembers.length}
              one="# member selected"
              other="# members selected"
            />
          ) : (
            t`Or choose manually`
          )}
        </Button>
      )}

      <Collapse
        unmountOnExit
        in={showMemberPicker}
        style={{ overflow: showMemberPicker ? 'unset' : 'hidden' }}
      >
        <MemberSelector
          usersInitiallyAvailableForAddition={availableMembers}
          selectedMembers={selectedMembers}
          onSelectedMembersUpdate={(_, newSelectedMembers) =>
            onManualSelectChange(newSelectedMembers)
          }
          disableEmailInvite
          selectedEmails={[]}
          onSelectedEmailsUpdate={() => {}}
        />
      </Collapse>
    </Stack>
  );
};
